import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ResumeVersion,
  type ResumeVersionPatch,
  type TailorRequest,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface TailorSection {
  label: string;
  units: Array<{ id: string }>;
}

export interface TailorResponse {
  version: ResumeVersion;
  sections: TailorSection[];
  model: string;
  job: { title: string; company_name: string };
}

export interface SwapResponse {
  version: ResumeVersion;
  sections: TailorSection[];
  replaces: string;
  dropped_unit_id: string;
}

export interface ResumeVersionsResponse {
  versions: ResumeVersion[];
}

export interface ResumeVersionResponse {
  version: ResumeVersion;
}

export interface ResumeVersionError {
  status: number;
  error: string;
  message?: string;
  detail?: string;
  retry_after_seconds?: number;
}

function toError(err: unknown): ResumeVersionError {
  if (err instanceof ApiError) {
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error:
        typeof payload.error === "string" ? payload.error : err.message ?? "request_failed",
      message:
        typeof payload.message === "string" ? payload.message : undefined,
      detail:
        typeof payload.detail === "string" ? payload.detail : undefined,
      retry_after_seconds:
        typeof payload.retry_after_seconds === "number"
          ? payload.retry_after_seconds
          : undefined,
    };
  }
  return { status: 0, error: "network_error" };
}

// ── Queries ────────────────────────────────────────────────────────────

export function useResumeVersions(opts: { applicationId?: string } = {}) {
  const qs = opts.applicationId ? `?application_id=${opts.applicationId}` : "";
  return useQuery({
    queryKey: ["resume-versions", opts.applicationId ?? "all"],
    queryFn: () => api<ResumeVersionsResponse>(`/api/resume-versions${qs}`),
    staleTime: 30_000,
  });
}

export function useResumeVersion(id: string | null) {
  return useQuery({
    queryKey: ["resume-versions", "one", id],
    enabled: !!id,
    queryFn: () => api<ResumeVersionResponse>(`/api/resume-versions/${id}`),
    staleTime: 30_000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────

export function useTailor() {
  const qc = useQueryClient();
  return useMutationToast<TailorResponse, ResumeVersionError, TailorRequest>({
    mutationFn: async (input) => {
      try {
        return await api<TailorResponse>("/api/resume-versions/tailor", {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resume-versions"] });
      qc.invalidateQueries({ queryKey: ["corpus"] });
    },
    success: (data) => `Tailored ${data.job.company_name} resume.`,
    errorPrefix: "Tailor failed",
  });
}

export function usePatchResumeVersion() {
  const qc = useQueryClient();
  return useMutationToast<
    ResumeVersionResponse,
    ResumeVersionError,
    { id: string; patch: ResumeVersionPatch }
  >({
    mutationFn: async ({ id, patch }) => {
      try {
        return await api<ResumeVersionResponse>(`/api/resume-versions/${id}`, {
          method: "PATCH",
          // Workshop edits are the user's own words: save them, and let
          // the approval audit flag anything the gate would reject.
          body: JSON.stringify({ ...patch, skip_quality_gate: true }),
        });
      } catch (err) {
        throw toError(err);
      }
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["resume-versions"] });
      qc.invalidateQueries({ queryKey: ["resume-versions", "one", id] });
    },
    // No success toast on save-on-blur edits — the workshop UI can be a
    // tight feedback loop and toasts on every blur are noisy.
    success: false,
    errorPrefix: "Save failed",
  });
}

export function useSwapUnit() {
  const qc = useQueryClient();
  return useMutationToast<
    SwapResponse,
    ResumeVersionError,
    { id: string; companyId: string; jobId: string; unitIdToDrop: string }
  >({
    mutationFn: async ({ id, companyId, jobId, unitIdToDrop }) => {
      try {
        return await api<SwapResponse>(`/api/resume-versions/${id}/swap`, {
          method: "POST",
          body: JSON.stringify({
            company_id: companyId,
            job_id: jobId,
            unit_id_to_drop: unitIdToDrop,
          }),
        });
      } catch (err) {
        throw toError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resume-versions"] });
      qc.invalidateQueries({ queryKey: ["corpus"] });
    },
    success: "Swapped unit and re-tailored.",
    errorPrefix: "Swap failed",
  });
}

export function useDeleteResumeVersion() {
  const qc = useQueryClient();
  return useMutationToast<{ ok: true }, ResumeVersionError, string>({
    mutationFn: async (id) => {
      try {
        return await api<{ ok: true }>(`/api/resume-versions/${id}`, {
          method: "DELETE",
        });
      } catch (err) {
        throw toError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resume-versions"] });
      qc.invalidateQueries({ queryKey: ["corpus"] });
    },
    success: "Resume version deleted.",
    errorPrefix: "Delete failed",
  });
}

// Build a download URL for the lazy-rendered PDF endpoint. The browser
// triggers the render-on-first-fetch via a plain anchor click with a `download`
// attribute, same as the cover-letter download surface.
export function resumeVersionPdfUrl(id: string): string {
  return `/api/resume-versions/${id}/pdf`;
}
